import { ParsedUrlQuery } from 'querystring';

import React from 'react';
import type { GetServerSideProps, NextPage } from 'next';
import Head from 'next/head';

import { withSessionSsr } from 'lib/auth';
import { ServerApiService } from 'services/ServerApiService';
import { ProductItem } from 'components/views/ProductItem';
import { StrapiService } from 'lib/strapi/StrapiService';
import { MagazineArticleCard } from 'components/sections/Magazine';
import { MarketplaceAdsResponse } from 'types/MarketplaceAdsResponse';
import { proofBuy } from 'content/mocked/proof';
import { getMarketplaceAdsParams } from 'utils/marketplaceAdsFilter';
import { useVehicleData } from 'lib/api/swr/useVehiclesData';

interface Props {
  data: any;
  articles: MagazineArticleCard[];
  marketplaceAds: MarketplaceAdsResponse;
}

interface Params extends ParsedUrlQuery {
  id: string;
}

const ProductPage: NextPage<Props> = ({ data, articles, marketplaceAds }) => {
  const marketplaceAdsParams = getMarketplaceAdsParams({
    perPage: 20,
    filters: {
      tag: '',
      ...(data.payload.vehicle.classification_en == null
        ? { extrapolated_category: [data.payload.vehicle.extrapolated_category] }
        : { classification_en: [data.payload.vehicle.classification_en] }),
    },
  });
  const {
    data: clientSideData,
    isLoading,
    error,
  } = useVehicleData(marketplaceAdsParams, marketplaceAds);

  return (
    <>
      <Head>
        <title>
          {data.payload.vehicle.brand}{' '}
          {data.payload.vehicle.model_gen_engine ?? data.payload.vehicle.model} - Blipp
        </title>
        <meta
          property="og:title"
          content={`${data.payload.vehicle.brand} ${
            data.payload.vehicle.model_gen_engine ?? data.payload.vehicle.model
          } - Blipp`}
        />
        {data.payload.cover_photo && (
          <meta
            property="og:image"
            key="og-image"
            content={`${data.payload.cover_photo.full_path}`}
          />
        )}
        <meta
          property="og:description"
          content="Köp med billån och experthjälp med hjälp av Blipp"
        />
        <meta name="description" content="Köp med billån och experthjälp med hjälp av Blipp" />
        {typeof window !== 'undefined' && (
          <link
            rel="canonical"
            key="canonical"
            href={`${window.location.protocol}//${window.location.host}/fordon/${data.payload.entity_id}`}
          />
        )}
      </Head>

      <ProductItem
        apiResponse={data}
        articles={articles}
        marketplaceAds={clientSideData}
        proof={proofBuy}
        isLoading={isLoading}
        error={error}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = withSessionSsr(
  async ({ req, res, params }) => {
    const { id } = params as Params;
    const service = new ServerApiService(req, res);
    const data = await service.marketplaceAd(id);

    if (data.status_code === 404) {
      return { notFound: true };
    }
    const articles = await StrapiService.getLatestMagazineArticles(3);

    return {
      props: {
        data,
        articles,
      },
    };
  }
);

export default ProductPage;
